export const RUNTIME_CONFIG_PATH = "/vida-cloud-surf-config.json";

/** Vida Cloud app id sent as `X-Vida-AppId` on AppSync requests. */
export const APP_ID = "surf";

export type RuntimeConfig = {
  appsyncEndpoint: string;
  appsyncRealtimeEndpoint?: string;
  clientConfigUrl?: string;
  bedrockProxyUrl?: string;
  pdfPresignedUrl?: string;
  ragSearchUrl?: string;
  cognitoDomain: string;
  cognitoClientId: string;
  oauthRedirectUri: string;
  oauthPostLogoutRedirectUri?: string;
  sessionBrokerUrl?: string;
  loginUrl?: string;
  publicBaseUrl?: string;
  cognitoIdentityPoolId?: string;
  cognitoUserPoolId?: string;
  awsRegion?: string;
  oceanAgentStreamUrl?: string;
  surfResearcherAgentId?: string;
  surfWhaleAgentId?: string;
};

let runtime: RuntimeConfig | null = null;
let endpointReadyResolve: (() => void) | null = null;
const endpointReadyPromise: Promise<void> = new Promise((resolve) => {
  endpointReadyResolve = resolve;
});

function notifyEndpointReady(): void {
  endpointReadyResolve?.();
  endpointReadyResolve = null;
}

export function waitForEndpointReady(): Promise<void> {
  if (getAppsyncEndpoint()) {
    notifyEndpointReady();
    return Promise.resolve();
  }
  if (import.meta.env.DEV) {
    notifyEndpointReady();
    return Promise.resolve();
  }
  return endpointReadyPromise;
}

function fromEnv(key: string): string {
  const raw = import.meta.env as Record<string, string | undefined>;
  return typeof raw[key] === "string" ? raw[key]!.trim() : "";
}

export async function ensureConfig(): Promise<void> {
  if (import.meta.env.DEV) {
    notifyEndpointReady();
    return;
  }
  if (runtime) {
    notifyEndpointReady();
    return;
  }
  try {
    const res = await fetch(RUNTIME_CONFIG_PATH, { cache: "no-store" });
    if (res.ok) {
      runtime = (await res.json()) as RuntimeConfig;
      notifyEndpointReady();
      return;
    }
  } catch {
    /* fallback */
  }
  if (
    fromEnv("VITE_APPSYNC_ENDPOINT") &&
    fromEnv("VITE_COGNITO_DOMAIN") &&
    fromEnv("VITE_COGNITO_CLIENT_ID") &&
    fromEnv("VITE_OAUTH_REDIRECT_URI")
  ) {
    notifyEndpointReady();
    return;
  }
  throw new Error(
    `Config mancante: serve ${RUNTIME_CONFIG_PATH} o variabili VITE_* in build.`,
  );
}

export function getAppsyncEndpoint(): string {
  return (
    runtime?.appsyncEndpoint?.trim() || fromEnv("VITE_APPSYNC_ENDPOINT")
  ).replace(/\/$/, "");
}

export function getAppsyncRealtimeEndpoint(): string {
  const fromRuntime = runtime?.appsyncRealtimeEndpoint?.trim();
  if (fromRuntime) return fromRuntime.replace(/\/$/, "");
  const env = fromEnv("VITE_APPSYNC_REALTIME_ENDPOINT");
  if (env) return env.replace(/\/$/, "");
  // Empty → presentChannel derives the realtime host from the GraphQL endpoint.
  return "";
}

export function getCognitoDomain(): string {
  return (
    runtime?.cognitoDomain?.trim() || fromEnv("VITE_COGNITO_DOMAIN")
  ).replace(/\/$/, "");
}

export function getClientId(): string {
  return runtime?.cognitoClientId?.trim() || fromEnv("VITE_COGNITO_CLIENT_ID");
}

export function getRedirectUri(): string {
  return (
    runtime?.oauthRedirectUri?.trim() || fromEnv("VITE_OAUTH_REDIRECT_URI")
  );
}

export function getPublicBaseUrl(): string {
  const fromRuntime = runtime?.publicBaseUrl?.trim();
  if (fromRuntime) return fromRuntime.replace(/\/$/, "");
  const env = fromEnv("VITE_PUBLIC_BASE_URL");
  if (env) return env.replace(/\/$/, "");
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

export function getSessionBrokerUrl(): string {
  return runtime?.sessionBrokerUrl?.trim() || fromEnv("VITE_SESSION_BROKER_URL");
}

export function getLoginUrl(): string {
  return runtime?.loginUrl?.trim() || fromEnv("VITE_LOGIN_URL");
}

export function getPostLogoutRedirect(): string {
  const login = getLoginUrl();
  if (login) return `${login.replace(/\/$/, "")}/login`;
  const fromRuntime = runtime?.oauthPostLogoutRedirectUri?.trim();
  if (fromRuntime) return fromRuntime;
  const envLogout = fromEnv("VITE_OAUTH_POST_LOGOUT_REDIRECT");
  if (envLogout) return envLogout;
  return "https://login.oceany.ai/login";
}

export function getClientConfigUrl(): string {
  return runtime?.clientConfigUrl?.trim() || fromEnv("VITE_CLIENT_CONFIG_URL");
}

export function getBedrockProxyUrlFromRuntime(): string {
  return (runtime?.bedrockProxyUrl?.trim() || fromEnv("VITE_BEDROCK_PROXY_URL")).replace(
    /\/+$/,
    "",
  );
}

export function getPdfPresignedUrlFromRuntime(): string {
  return (runtime?.pdfPresignedUrl?.trim() || fromEnv("VITE_PDF_PRESIGNED_URL")).replace(
    /\/+$/,
    "",
  );
}

export function getRagSearchUrlFromRuntime(): string {
  return (runtime?.ragSearchUrl?.trim() || fromEnv("VITE_RAG_SEARCH_URL")).replace(/\/+$/, "");
}

export function getOceanAgentStreamUrlFromRuntime(): string {
  return (
    runtime?.oceanAgentStreamUrl?.trim() || fromEnv("VITE_OCEAN_AGENT_STREAM_URL")
  ).replace(/\/+$/, "");
}

export function getSurfResearcherAgentIdFromRuntime(): string {
  return runtime?.surfResearcherAgentId?.trim() || fromEnv("VITE_SURF_RESEARCHER_AGENT_ID");
}

export function getSurfWhaleAgentIdFromRuntime(): string {
  return runtime?.surfWhaleAgentId?.trim() || fromEnv("VITE_SURF_WHALE_AGENT_ID");
}

export function getCognitoIdentityPoolIdFromRuntime(): string {
  return runtime?.cognitoIdentityPoolId?.trim() || fromEnv("VITE_COGNITO_IDENTITY_POOL_ID");
}

export function getCognitoUserPoolIdFromRuntime(): string {
  return runtime?.cognitoUserPoolId?.trim() || fromEnv("VITE_COGNITO_USER_POOL_ID");
}

export function getAwsRegionFromRuntime(): string {
  return runtime?.awsRegion?.trim() || fromEnv("VITE_AWS_REGION");
}

export function isConfigured(): boolean {
  return Boolean(
    getAppsyncEndpoint() &&
      getCognitoDomain() &&
      getClientId(),
  );
}
